/**
 * Zod schemas matching backend Pydantic models.
 * Every API response is validated before use.
 */

import { z } from "zod";

// ── Auth ─────────────────────────────────────────────────

export const LoginResponseSchema = z.object({
  user_id: z.string().uuid(),
  company_id: z.string().uuid().nullable(),
  status: z.string(),
  is_global_admin: z.boolean(),
  is_company_admin: z.boolean(),
  must_change_password: z.boolean(),
  mfa_enabled: z.boolean(),
  must_setup_totp: z.boolean().default(false),
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export const MFASetupResponseSchema = z.object({
  qr_code: z.string(),
  provisioning_uri: z.string(),
});
export type MFASetupResponse = z.infer<typeof MFASetupResponseSchema>;

export const RefreshResponseSchema = z.object({
  message: z.string(),
});

// ── Error ────────────────────────────────────────────────

export const APIErrorSchema = z.object({
  error: z.string(),
  message: z.string(),
});
export type APIError = z.infer<typeof APIErrorSchema>;

export const SessionConflictSchema = z.object({
  error: z.literal("session_conflict"),
  message: z.string(),
  active_device: z.string(),
  last_activity: z.string(),
});
export type SessionConflict = z.infer<typeof SessionConflictSchema>;

export const MFARequiredSchema = z.object({
  error: z.literal("mfa_required"),
  message: z.string(),
  user_id: z.string().uuid(),
  // Ordered list of MFA methods the user has configured (primary first).
  // e.g. ["webauthn", "totp"] or ["totp"]. Global Admin only.
  mfa_methods: z.array(z.string()).optional().default(["totp"]),
});
export type MFARequired = z.infer<typeof MFARequiredSchema>;

export const RateLimitSchema = z.object({
  error: z.literal("rate_limit_exceeded"),
  message: z.string(),
  retry_after: z.number(),
});

// ── Password reset ──────────────────────────────────────

export const PasswordResetRequestResponseSchema = z.object({
  message: z.string(),
});
export type PasswordResetRequestResponse = z.infer<
  typeof PasswordResetRequestResponseSchema
>;

export const PasswordResetConfirmResponseSchema = z.object({
  message: z.string(),
});

// ── Me ──────────────────────────────────────────────────

// ── Modules ─────────────────────────────────────────────
// Hardcoded set of product modules. Both backend and frontend keep this
// enum in lockstep; new modules require code changes on both sides.
export const ModuleType = {
  Marketing: "marketing",
} as const;
export type ModuleType = (typeof ModuleType)[keyof typeof ModuleType];

export const ModuleTypeSchema = z.enum([ModuleType.Marketing]);

/**
 * Company-level metadata surfaced on ``/auth/me`` alongside the user.
 *
 * Mirrors the backend ``MeResponseCompany`` Pydantic model shipped in
 * PR-5. Consumed by ``ObservabilityBridge`` to populate PostHog Group
 * Properties on ``company`` (see ``plans/PLAN_POSTHOG_01_FOUNDATIONS.md``
 * § "Group Properties").
 *
 * ``created_at`` arrives as an ISO 8601 string (Pydantic v2 default
 * serialisation). Consumers that need a Date can ``new Date(...)`` at
 * the call site; we keep the wire shape as a string so the Zod schema
 * stays a value-type and round-trips cleanly through ``JSON.stringify``
 * + ``JSON.parse``.
 */
export const MeResponseCompanySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  status: z.string(),
  setup_complete: z.boolean(),
  marketing_licenses: z.number().int().nullable(),
  company_modules: z.array(z.string()),
  country: z.string().nullable(),
  timezone: z.string().nullable(),
  sso_mode: z.string().nullable(),
  created_at: z.string(),
});
export type MeResponseCompany = z.infer<typeof MeResponseCompanySchema>;

export const MeResponseSchema = z.object({
  user_id: z.string().uuid(),
  company_id: z.string().uuid().nullable(),
  email: z.string(),
  full_name: z.string(),
  status: z.string(),
  is_global_admin: z.boolean(),
  is_company_admin: z.boolean(),
  // Effective module set already resolved server-side.
  enabled_modules: z.array(z.string()),
  must_change_password: z.boolean(),
  mfa_enabled: z.boolean(),
  timezone: z.string().nullable().optional(),
  // Populated for company users (CA + module users); ``null`` for
  // global admins. ``optional()`` for backwards compatibility with
  // deployments where the backend hasn't shipped PR-5 yet.
  company: MeResponseCompanySchema.nullable().optional(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

// ── Address ─────────────────────────────────────────────

export const AddressSchema = z.object({
  line1: z.string(),
  line2: z.string().nullable().optional(),
  city: z.string(),
  state: z.string().nullable().optional(),
  postal_code: z.string(),
  country: z.string().length(2),
});
export type Address = z.infer<typeof AddressSchema>;

// ── Pagination ──────────────────────────────────────────

export function PaginatedSchema<T extends z.ZodTypeAny>(itemSchema: T) {
  return z.object({
    items: z.array(itemSchema),
    total: z.number(),
    skip: z.number(),
    limit: z.number(),
  });
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  skip: number;
  limit: number;
}

// ── Company ──────────────────────────────────────────────

export const LogoUrlsSchema = z.object({
  icon: z.string().url(),
  standard: z.string().url(),
  report: z.string().url(),
});
export type LogoUrls = z.infer<typeof LogoUrlsSchema>;

export const CompanySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  status: z.string(),
  setup_complete: z.boolean().optional().default(false),
  security_configured_at: z.string().nullable().optional(),
  allowed_domains: z.array(z.string()),
  // Modules this company is licensed for (slugs). Marketing is the only
  // module today; new modules join the enum + this list as they ship.
  modules: z.array(z.string()),
  marketing_licenses: z.number(),
  legal_name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  timezone: z.string().nullable().optional(),
  vat_id: z.string().nullable().optional(),
  company_address: AddressSchema.nullable().optional(),
  billing_address: AddressSchema.nullable().optional(),
  billing_email: z.string().nullable().optional(),
  require_mfa: z.boolean(),
  password_max_age_days: z.number(),
  password_history_count: z.number(),
  security_notifications_enabled: z.boolean().optional().default(true),
  ip_allowlist_enabled: z.boolean().optional().default(false),
  ip_allowlist: z.array(z.string()).optional().default([]),
  logo_version: z.number().optional().default(0),
  logo_urls: LogoUrlsSchema.nullable().optional(),
  logo_uploaded_at: z.string().nullable().optional(),
  // Jurisdiction identity (captured at GA setup, validated per
  // country at the application layer).
  legal_form: z.string().nullable().optional(),
  registry_name: z.string().nullable().optional(),
  registry_id: z.string().nullable().optional(),
  // Master-contract gate. Null = the offline-signed master contract
  // has not been recorded; the AgreementGate keeps acceptance locked
  // behind an "awaiting master contract" overlay until the GA fills
  // these via PATCH /admin/companies/{id}/master-contract.
  master_contract_signed_at: z.string().nullable().optional(),
  master_contract_signed_by: z.string().nullable().optional(),
  // SHA-256 hex of the offline-signed PDF — tamper-evidence anchor
  // exposed for the GA console "Recorded fingerprint" display. Pure
  // fingerprint: reveals nothing about the contract's content.
  master_contract_pdf_sha256: z.string().nullable().optional(),
});
export type Company = z.infer<typeof CompanySchema>;

// ── User ─────────────────────────────────────────────────

export const UserRoleSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  is_system_role: z.boolean(),
});
export type UserRoleSummary = z.infer<typeof UserRoleSummarySchema>;

// Most-recent SSO login facts. Populated only for users who've actually
// signed in via SSO at least once. ``last_amr`` is the source-of-truth
// answer for "did this user have MFA at last sign-in" — the admin UI
// renders it as badges so a GA can answer the SOC2 question at a glance.
export const SsoLoginFactsSummarySchema = z.object({
  last_login_at: z.string(),
  last_amr: z.array(z.string()),
});
export type SsoLoginFactsSummary = z.infer<typeof SsoLoginFactsSummarySchema>;

export const UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  full_name: z.string(),
  status: z.string(),
  is_company_admin: z.boolean(),
  is_global_admin: z.boolean(),
  // Explicit user-level module grants (slugs). CAs and GAs typically have
  // an empty list because they bypass user-level checks by role — admin
  // surfaces should render their effective access from role + company
  // modules, not from this field.
  modules: z.array(z.string()),
  mfa_enabled: z.boolean(),
  must_change_password: z.boolean(),
  last_login_at: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  role: UserRoleSummarySchema.nullable().optional(),
  // SSO identity — null for password users, 'entra_id' for Microsoft-linked.
  idp_provider: z.string().nullable().optional().default(null),
  // Latest SSO login facts (admin endpoints only populate this; CA-side
  // endpoints leave it ``null``). ``null`` also for SSO-linked users who
  // have not yet completed an SSO sign-in.
  sso_login_facts: SsoLoginFactsSummarySchema.nullable().optional().default(null),
});
export type User = z.infer<typeof UserSchema>;

// ── Roles ────────────────────────────────────────────────

export const RoleSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  permissions: z.record(z.string(), z.unknown()),
  is_system_role: z.boolean(),
});
export type Role = z.infer<typeof RoleSchema>;

// ── Invitation ───────────────────────────────────────────

export const InvitationSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  status: z.string(),
  created_at: z.string(),
  expires_at: z.string(),
  last_resent_at: z.string().nullable().optional(),
  role_id: z.string().uuid().nullable().optional(),
  is_admin_onboarding: z.boolean().optional(),
  granted_modules: z.array(z.string()).optional().default([]),
});
export type Invitation = z.infer<typeof InvitationSchema>;

export const SeatUsageEntrySchema = z.object({
  module: z.string(),
  enabled: z.boolean(),
  licenses: z.number(),
  used: z.number(),
  pending: z.number(),
});
export const SeatUsageSchema = z.object({
  modules: z.array(SeatUsageEntrySchema),
});
export type SeatUsage = z.infer<typeof SeatUsageSchema>;
export type SeatUsageEntry = z.infer<typeof SeatUsageEntrySchema>;

// ── Admin (Global Admin) ────────────────────────────────

export const AdminUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  full_name: z.string(),
  status: z.string(),
  is_global_admin: z.boolean(),
  mfa_enabled: z.boolean(),
});
export type AdminUser = z.infer<typeof AdminUserSchema>;

export const GlobalAdminInvitationSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  status: z.string(),
  created_at: z.string(),
  expires_at: z.string(),
  last_resent_at: z.string().nullable().optional(),
  invited_by_email: z.string().nullable().optional(),
  invited_by_name: z.string().nullable().optional(),
});
export type GlobalAdminInvitation = z.infer<typeof GlobalAdminInvitationSchema>;

export const CompanyReadinessSchema = z.object({
  is_ready: z.boolean(),
  must_haves: z.record(z.string(), z.boolean()),
  should_haves: z.record(z.string(), z.boolean()),
  missing_must_haves: z.array(z.string()),
});
export type CompanyReadiness = z.infer<typeof CompanyReadinessSchema>;

export const CompanyDetailSchema = CompanySchema.extend({
  user_count: z.number(),
  // Platform kill-switch surface (backend Settings.disabled_modules) —
  // drives the greyed-out module toggle in the admin UI.
  platform_disabled_modules: z.array(z.string()).optional().default([]),
  marketing_users_count: z.number().optional().default(0),
  marketing_utilization_pct: z.number().optional().default(0),
  readiness: CompanyReadinessSchema.nullable().optional(),
});
export type CompanyDetail = z.infer<typeof CompanyDetailSchema>;

// ── Placements ──────────────────────────────────────────

export const CampaignSchema = z.object({
  id: z.string().uuid(),
  company_id: z.string().uuid(),
  created_by: z.string().uuid().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  status: z.string(),
  target_countries: z.array(z.string()),
  target_segments: z.record(z.string(), z.unknown()),
  daily_budget_cents: z.number().nullable(),
  total_budget_cents: z.number().nullable(),
  starts_at: z.string().nullable(),
  ends_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Campaign = z.infer<typeof CampaignSchema>;

export const CreativeSchema = z.object({
  id: z.string().uuid(),
  campaign_id: z.string().uuid(),
  company_id: z.string().uuid(),
  name: z.string(),
  creative_type: z.string(),
  asset_url: z.string().nullable(),
  headline: z.string().nullable(),
  body_text: z.string().nullable(),
  call_to_action: z.string().nullable(),
  click_url: z.string().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  is_active: z.boolean(),
  impressions: z.number(),
  clicks: z.number(),
  spend_cents: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Creative = z.infer<typeof CreativeSchema>;

export const CampaignAnalyticsSchema = z.object({
  campaign_id: z.string().uuid(),
  campaign_name: z.string(),
  status: z.string(),
  total_impressions: z.number(),
  total_clicks: z.number(),
  total_spend_cents: z.number(),
  ctr: z.number(),
  creative_count: z.number(),
});
export type CampaignAnalytics = z.infer<typeof CampaignAnalyticsSchema>;

// ── MI Data — Dashboards ───────────────────────────────

export const DashboardVisibility = {
  private: "private",
  company: "company",
  specific: "specific",
} as const;
export type DashboardVisibility =
  (typeof DashboardVisibility)[keyof typeof DashboardVisibility];

export const DashboardPermissionSchema = z.object({
  user_id: z.string().uuid(),
  can_edit: z.boolean(),
  granted_at: z.string(),
});
export type DashboardPermission = z.infer<typeof DashboardPermissionSchema>;

export const DashboardSchema = z.object({
  id: z.string().uuid(),
  company_id: z.string().uuid(),
  created_by: z.string().uuid().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  widgets: z.array(z.record(z.string(), z.unknown())),
  is_default: z.boolean(),
  sort_order: z.number(),
  visibility: z.enum(["private", "company", "specific"]),
  company_can_edit: z.boolean(),
  permissions: z.array(DashboardPermissionSchema),
  can_edit: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Dashboard = z.infer<typeof DashboardSchema>;

// ── Dashboard Templates (Global Admin) ────────────────

export const DashboardTemplateSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  display_name: z.string().nullable().optional(),
  description: z.string().nullable(),
  widgets: z.array(z.record(z.string(), z.unknown())),
  category: z.string().nullable(),
  is_active: z.boolean(),
  created_by: z.string().uuid().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type DashboardTemplate = z.infer<typeof DashboardTemplateSchema>;

// ── MI Data — Queries ──────────────────────────────────

export const EventRowSchema = z.object({
  time: z.string(),
  category_id: z.number(),
  region_bucket: z.string(),
  asset_identifier: z.string().nullable(),
  asset_type: z.string().nullable(),
  session_hash: z.string(),
  event_count: z.number(),
  metadata: z.record(z.string(), z.unknown()),
});
export type EventRow = z.infer<typeof EventRowSchema>;

export const EventQueryResponseSchema = z.object({
  rows: z.array(EventRowSchema),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
});
export type EventQueryResponse = z.infer<typeof EventQueryResponseSchema>;

export const TimeseriesBucketSchema = z.object({
  bucket: z.string(),
  event_count: z.number(),
  unique_sessions: z.number(),
});
export type TimeseriesBucket = z.infer<typeof TimeseriesBucketSchema>;

export const TimeseriesResponseSchema = z.object({
  buckets: z.array(TimeseriesBucketSchema),
  total_events: z.number(),
  total_sessions: z.number(),
});
export type TimeseriesResponse = z.infer<typeof TimeseriesResponseSchema>;

export const EventCategorySchema = z.object({
  id: z.number(),
  slug: z.string(),
  display_name: z.string(),
  description: z.string().nullable(),
});
export type EventCategory = z.infer<typeof EventCategorySchema>;

// ── MI Data — Exports ──────────────────────────────────

export const ExportRecordSchema = z.object({
  id: z.string().uuid(),
  company_id: z.string().uuid(),
  created_by: z.string().uuid().nullable(),
  source: z.string().nullable(),
  format: z.string(),
  row_count: z.number(),
  column_count: z.number(),
  file_size_bytes: z.number(),
  filters: z.record(z.string(), z.unknown()),
  status: z.string(),
  created_at: z.string(),
});
export type ExportRecord = z.infer<typeof ExportRecordSchema>;

// ── Message Response ───────────────────────────────────

export const MessageResponseSchema = z.object({
  message: z.string(),
});
export type MessageResponse = z.infer<typeof MessageResponseSchema>;

// ── Sessions ────────────────────────────────────────────

export const SessionSchema = z.object({
  id: z.string().uuid(),
  device_name: z.string(),
  ip_address: z.string(),
  geo_country: z.string().nullable().optional(),
  geo_city: z.string().nullable().optional(),
  created_at: z.string(),
  last_activity_at: z.string(),
  expires_at: z.string(),
  // True for the session this request is authenticated by — the UI uses it
  // to warn the user before revoking the device they're sitting at.
  is_current: z.boolean().default(false),
});
export type Session = z.infer<typeof SessionSchema>;

// ── Audit Logs ──────────────────────────────────────────

export const AuditLogEntrySchema = z.object({
  id: z.number(),
  created_at: z.string(),
  company_id: z.string().nullable().optional(),
  user_id: z.string().nullable(),
  user_email: z.string().nullable().optional(),
  user_full_name: z.string().nullable().optional(),
  actor_type: z.string(),
  action_category: z.string(),
  action: z.string(),
  resource_type: z.string(),
  resource_id: z.string().nullable(),
  details: z.record(z.string(), z.unknown()),
  ip_address: z.string().nullable(),
  geo_country: z.string().nullable().optional(),
  geo_city: z.string().nullable().optional(),
  geo_lat: z.number().nullable().optional(),
  geo_lon: z.number().nullable().optional(),
});
export type AuditLogEntry = z.infer<typeof AuditLogEntrySchema>;

export const AuditLogResponseSchema = z.object({
  items: z.array(AuditLogEntrySchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});
export type AuditLogResponse = z.infer<typeof AuditLogResponseSchema>;

export const AuditFilterOptionsSchema = z.object({
  actions: z.array(z.string()),
  categories: z.array(z.string()),
  actor_types: z.array(z.string()),
});
export type AuditFilterOptions = z.infer<typeof AuditFilterOptionsSchema>;

// ── WebAuthn (Global Admin only) ───────────────────────

export const WebAuthnCredentialSchema = z.object({
  id: z.string().uuid(),
  device_name: z.string(),
  is_primary: z.boolean(),
  transports: z.array(z.string()),
  created_at: z.string(),
  last_used_at: z.string().nullable(),
});
export type WebAuthnCredential = z.infer<typeof WebAuthnCredentialSchema>;

// ── Login History ──────────────────────────────────────

export const LoginHistoryEntrySchema = z.object({
  created_at: z.string(),
  action: z.string(),
  ip_address: z.string().nullable(),
  geo_country: z.string().nullable().optional(),
  geo_city: z.string().nullable().optional(),
  // Anomaly flags derived from audit details server-side. UI renders a
  // badge when either is true so the user can spot unusual logins at a
  // glance without having to compare geolocations by eye.
  new_device: z.boolean().optional().default(false),
  new_country: z.boolean().optional().default(false),
});
export type LoginHistoryEntry = z.infer<typeof LoginHistoryEntrySchema>;

export const LoginHistoryResponseSchema = z.object({
  items: z.array(LoginHistoryEntrySchema),
});
export type LoginHistoryResponse = z.infer<typeof LoginHistoryResponseSchema>;

// ── API Keys ──────────────────────────────────────────

export const APIKeySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  key_prefix: z.string(),
  permissions: z.record(z.string(), z.unknown()),
  allowed_ips: z.array(z.string()).nullable(),
  rate_limit_per_minute: z.number(),
  is_active: z.boolean(),
  last_used_at: z.string().nullable(),
  expires_at: z.string().nullable(),
  created_at: z.string(),
  total_request_count: z.number(),
});
export type APIKey = z.infer<typeof APIKeySchema>;

export const APIKeyCreatedSchema = APIKeySchema.extend({
  key: z.string(),
});
export type APIKeyCreated = z.infer<typeof APIKeyCreatedSchema>;

export const APIKeyUsageBucketSchema = z.object({
  bucket: z.string(),
  request_count: z.number(),
});

export const APIKeyUsageSchema = z.object({
  key_id: z.string().uuid(),
  total: z.number(),
  last_7d: z.number(),
  last_30d: z.number(),
  sparkline_48h: z.array(APIKeyUsageBucketSchema),
});
export type APIKeyUsage = z.infer<typeof APIKeyUsageSchema>;

// ── Assets (Products) ──────────────────────────────────

export const AssetSearchResultSchema = z.object({
  asset_type: z.string(),
  symbol: z.string(),
  name: z.string(),
  exchange: z.string().nullable(),
  currency: z.string().nullable(),
  coingecko_id: z.string().nullable(),
  market_cap_rank: z.number().nullable().optional(),
  category: z.string().nullable().optional(),
});
export type AssetSearchResult = z.infer<typeof AssetSearchResultSchema>;

export const AssetSearchResponseSchema = z.object({
  results: z.array(AssetSearchResultSchema),
  count: z.number(),
  query: z.string(),
  identifier_detected: z.string().nullable().optional(),
  source_path: z.string().nullable().optional(),
  cache_hit: z.boolean().optional(),
});
export type AssetSearchResponse = z.infer<typeof AssetSearchResponseSchema>;

export const AssetSchema = z.object({
  id: z.string().uuid(),
  asset_type: z.string(),
  symbol: z.string(),
  name: z.string(),
  exchange: z.string().nullable().optional(),
  currency: z.string().nullable().optional(),
  logo_url: z.string().nullable().optional(),
  coingecko_id: z.string().nullable().optional(),
  // Tier 1 static enrichment
  sector: z.string().nullable().optional(),
  sector_key: z.string().nullable().optional(),
  industry: z.string().nullable().optional(),
  provider: z.string().nullable().optional(),
  is_mutual_fund: z.boolean().optional(),
  data_availability: z.string().optional(),
  // Tier 2 live price
  price: z.number().nullable().optional(),
  change: z.number().nullable().optional(),
  change_percent: z.number().nullable().optional(),
  market_cap: z.number().nullable().optional(),
  aum: z.number().nullable().optional(),
  expense_ratio: z.number().nullable().optional(),
  dividend_yield: z.number().nullable().optional(),
  pe_ratio: z.number().nullable().optional(),
  volume: z.number().nullable().optional(),
  fifty_two_week_high: z.number().nullable().optional(),
  fifty_two_week_low: z.number().nullable().optional(),
  beta: z.number().nullable().optional(),
  holdings_count: z.number().nullable().optional(),
});
export type Asset = z.infer<typeof AssetSchema>;

export const CompanyAssetSchema = z.object({
  id: z.string().uuid(),
  asset: AssetSchema,
  tags: z.array(z.string()),
  notes: z.string().nullable(),
  added_by: z.string().uuid().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type CompanyAsset = z.infer<typeof CompanyAssetSchema>;

export const PaginatedCompanyAssetsSchema = z.object({
  items: z.array(CompanyAssetSchema),
  total: z.number(),
  skip: z.number(),
  limit: z.number(),
});
export type PaginatedCompanyAssets = z.infer<typeof PaginatedCompanyAssetsSchema>;

export const CompanyAssetTagsSchema = z.object({
  tags: z.array(z.string()),
});
export type CompanyAssetTags = z.infer<typeof CompanyAssetTagsSchema>;

// Full detail (Tier 3 — for /products/[id] page)
export const PerformanceDataSchema = z.object({
  "1d": z.number().nullable().optional(),
  "1w": z.number().nullable().optional(),
  "1m": z.number().nullable().optional(),
  "3m": z.number().nullable().optional(),
  "6m": z.number().nullable().optional(),
  ytd: z.number().nullable().optional(),
  "1y": z.number().nullable().optional(),
  "3y": z.number().nullable().optional(),
  "5y": z.number().nullable().optional(),
});
export type PerformanceData = z.infer<typeof PerformanceDataSchema>;

export const AnalystRatingSchema = z.object({
  rating: z.string().nullable().optional(),
  score: z.number().nullable().optional(),
  target_price: z.number().nullable().optional(),
  target_high: z.number().nullable().optional(),
  target_low: z.number().nullable().optional(),
  strong_buy: z.number().nullable().optional(),
  buy: z.number().nullable().optional(),
  hold: z.number().nullable().optional(),
  sell: z.number().nullable().optional(),
  strong_sell: z.number().nullable().optional(),
  total_analysts: z.number().nullable().optional(),
});
export type AnalystRating = z.infer<typeof AnalystRatingSchema>;

export const PeerSchema = z.object({
  symbol: z.string(),
  name: z.string().nullable().optional(),
  price: z.number().nullable().optional(),
  change_percent: z.number().nullable().optional(),
  logo_url: z.string().nullable().optional(),
});

export const SectorWeightingSchema = z.object({
  sector: z.string(),
  weight_percentage: z.number(),
});

export const CountryWeightingSchema = z.object({
  country: z.string(),
  weight_percentage: z.number(),
});

export const HoldingSchema = z.object({
  asset: z.string(),
  name: z.string(),
  weight_percentage: z.number(),
  market_value: z.number().nullable().optional(),
  asset_type: z.string().optional(),
});

export const DividendInfoSchema = z.object({
  yield_ttm: z.number().nullable().optional(),
  frequency: z.string().nullable().optional(),
  last_payment_date: z.string().nullable().optional(),
  last_payment_amount: z.number().nullable().optional(),
});

export const AssetDetailSchema = AssetSchema.extend({
  description: z.string().nullable().optional(),
  website: z.string().nullable().optional(),
  ceo: z.string().nullable().optional(),
  employees: z.number().nullable().optional(),
  founded: z.string().nullable().optional(),
  forward_pe: z.number().nullable().optional(),
  peg_ratio: z.number().nullable().optional(),
  price_to_book: z.number().nullable().optional(),
  price_to_sales: z.number().nullable().optional(),
  enterprise_value: z.number().nullable().optional(),
  ev_to_ebitda: z.number().nullable().optional(),
  domicile: z.string().nullable().optional(),
  asset_class: z.string().nullable().optional(),
  replication_method: z.string().nullable().optional(),
  distribution_policy: z.string().nullable().optional(),
  inception_date: z.string().nullable().optional(),
  fund_manager: z.string().nullable().optional(),
  performance: PerformanceDataSchema.nullable().optional(),
  financials: z.record(z.string(), z.unknown()).nullable().optional(),
  analyst_rating: AnalystRatingSchema.nullable().optional(),
  peers: z.array(PeerSchema).nullable().optional(),
  sector_weightings: z.array(SectorWeightingSchema).nullable().optional(),
  country_weightings: z.array(CountryWeightingSchema).nullable().optional(),
  top_holdings: z.array(HoldingSchema).nullable().optional(),
  dividend_info: DividendInfoSchema.nullable().optional(),
});
export type AssetDetail = z.infer<typeof AssetDetailSchema>;

export const CompanyAssetDetailSchema = z.object({
  id: z.string().uuid(),
  asset: AssetDetailSchema,
  tags: z.array(z.string()),
  notes: z.string().nullable(),
  added_by: z.string().uuid().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type CompanyAssetDetail = z.infer<typeof CompanyAssetDetailSchema>;

// ── Agreements (ToS / DPA) ────────────────────────────

export const AgreementSchema = z.object({
  id: z.string().uuid(),
  company_id: z.string().uuid(),
  agreement_type: z.string(),
  version: z.string(),
  accepted_by: z.string().uuid().nullable().optional(),
  accepted_by_name: z.string().nullable().optional(),
  accepted_by_email: z.string().nullable().optional(),
  accepted_at: z.string(),
  ip_address: z.string().nullable().optional(),
  geo_country: z.string().nullable().optional(),
  geo_city: z.string().nullable().optional(),
  user_agent: z.string().nullable().optional(),
  // SHA-256 of the canonical MDX bytes the user accepted. Compared
  // against the current /api/legal-integrity hash to surface drift
  // ("the bytes I accepted no longer match what's in production").
  document_hash: z.string().nullable().optional(),
});
export type Agreement = z.infer<typeof AgreementSchema>;

export const AgreementStatusSchema = z.object({
  tos_accepted: z.boolean(),
  tos_current_version: z.string(),
  tos_accepted_version: z.string().nullable().optional(),
  tos_accepted_at: z.string().nullable().optional(),
  dpa_accepted: z.boolean(),
  dpa_current_version: z.string(),
  dpa_accepted_version: z.string().nullable().optional(),
  dpa_accepted_at: z.string().nullable().optional(),
  // Null until the GA records the offline-signed master contract on
  // the platform. While null, the AgreementGate keeps the per-doc
  // Accept buttons hidden behind an "awaiting master contract"
  // overlay regardless of tos_accepted / dpa_accepted state.
  master_contract_signed_at: z.string().nullable().optional(),
});
export type AgreementStatus = z.infer<typeof AgreementStatusSchema>;

export const OnboardingStatusSchema = z.object({
  admin_invited: z.boolean(),
  admin_accepted: z.boolean(),
  mfa_configured: z.boolean(),
  security_configured: z.boolean(),
  agreements_accepted: z.boolean(),
  first_data_accessed: z.boolean(),
  products_configured: z.boolean(),
});
export type OnboardingStatus = z.infer<typeof OnboardingStatusSchema>;

// ── Platform Dashboard ────────────────────────────────

export const ModuleLicenseAggregateSchema = z.object({
  module: z.string(),
  sold: z.number(),
  used: z.number(),
});
export type ModuleLicenseAggregate = z.infer<typeof ModuleLicenseAggregateSchema>;

export const PlatformDashboardSchema = z.object({
  total_companies: z.number(),
  active_companies: z.number(),
  overdue_companies: z.number(),
  deactivated_companies: z.number(),
  // Per-module aggregate license counts. One entry per module that has a
  // seat cap; new modules add new entries as they ship.
  module_licenses: z.array(ModuleLicenseAggregateSchema),
  active_users_7d: z.number(),
  companies_onboarding_incomplete: z.number(),
});
export type PlatformDashboard = z.infer<typeof PlatformDashboardSchema>;

// ── Company Notes ─────────────────────────────────────

export const CompanyNoteSchema = z.object({
  id: z.string().uuid(),
  company_id: z.string().uuid(),
  author_id: z.string().uuid().nullable().optional(),
  author_name: z.string().nullable().optional(),
  author_email: z.string().nullable().optional(),
  content: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type CompanyNote = z.infer<typeof CompanyNoteSchema>;

// ── User Activity ─────────────────────────────────────

export const UserActivitySchema = z.object({
  user_id: z.string().uuid(),
  email: z.string(),
  full_name: z.string(),
  last_login: z.string().nullable().optional(),
  logins_30d: z.number(),
  data_queries_30d: z.number(),
  exports_30d: z.number(),
});
export type UserActivity = z.infer<typeof UserActivitySchema>;

// ── License Summary ───────────────────────────────────

export const LicenseUserInfoSchema = z.object({
  id: z.string().uuid(),
  full_name: z.string(),
  email: z.string(),
  last_login_at: z.string().nullable().optional(),
});

// Per-module license usage. Shape mirrors the backend
// `ModuleLicenseSummary` — the company licensing model is now
// module-keyed (one entry per module with a seat cap), not the
// legacy fixed `{ mi, placements }` pair. `module` is kept a plain
// string so an unknown module slug can't break the whole Plan tab.
export const ModuleLicenseSummarySchema = z.object({
  module: z.string(),
  enabled: z.boolean(),
  licenses_total: z.number(),
  licenses_used: z.number(),
  users: z.array(LicenseUserInfoSchema),
});
export type ModuleLicenseSummary = z.infer<typeof ModuleLicenseSummarySchema>;

export const LicenseSummarySchema = z.object({
  modules: z.array(ModuleLicenseSummarySchema),
});
export type LicenseSummary = z.infer<typeof LicenseSummarySchema>;

// ── Entitlements & Pricing ──────────────────────────────

export const RegionPresetInfoSchema = z.object({
  name: z.string(),
  label: z.string(),
  countries: z.array(z.string().length(2)),
});
export type RegionPresetInfo = z.infer<typeof RegionPresetInfoSchema>;

export const EntitlementCatalogSchema = z.object({
  region_presets: z.array(RegionPresetInfoSchema),
  asset_classes: z.array(z.string()),
  main_served_countries: z.array(z.string().length(2)),
  currencies: z.array(z.string().length(3)),
});
export type EntitlementCatalog = z.infer<typeof EntitlementCatalogSchema>;

export const EntitlementResponseSchema = z.object({
  company_id: z.string().uuid(),
  regions_included: z.array(z.string()),
  countries_included: z.array(z.string().length(2)),
  countries_excluded: z.array(z.string().length(2)),
  asset_classes: z.array(z.string()),
  effective_countries: z.array(z.string().length(2)),
  version: z.number(),
  granted_by: z.string().uuid().nullable(),
  notes: z.string().nullable(),
  updated_at: z.string(),
  created_at: z.string(),
});
export type EntitlementResponse = z.infer<typeof EntitlementResponseSchema>;

export const CompanyScopeSchema = z.object({
  regions_included: z.array(z.string()),
  countries_included: z.array(z.string().length(2)),
  countries_excluded: z.array(z.string().length(2)),
  asset_classes: z.array(z.string()),
  effective_countries: z.array(z.string().length(2)),
});
export type CompanyScope = z.infer<typeof CompanyScopeSchema>;

export const PricingResponseSchema = z.object({
  company_id: z.string().uuid(),
  base_fee_monthly: z.string(),
  license_fee_monthly: z.string(),
  currency: z.string().length(3),
  billing_mode: z.string(),
  stripe_subscription_id: z.string().nullable(),
  stripe_price_id: z.string().nullable(),
  notes: z.string().nullable(),
  updated_at: z.string(),
  updated_by: z.string().uuid().nullable(),
});
export type PricingResponse = z.infer<typeof PricingResponseSchema>;

export const EntitlementHistoryEntrySchema = z.object({
  id: z.string().uuid(),
  company_id: z.string().uuid(),
  version: z.number(),
  regions_included: z.array(z.string()),
  countries_included: z.array(z.string().length(2)),
  countries_excluded: z.array(z.string().length(2)),
  asset_classes: z.array(z.string()),
  changed_by: z.string().uuid().nullable(),
  change_reason: z.string(),
  changed_at: z.string(),
});
export type EntitlementHistoryEntry = z.infer<typeof EntitlementHistoryEntrySchema>;
